import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { UserApiService } from '@entities/user/data-access/user.api-service';
import { Observable, tap } from 'rxjs';
import { CurrentUserPermissionsDto, Permission, PermissionMap, SystemRole } from '../models/permissions.models';

@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  private readonly _userApiService = inject(UserApiService);

  private readonly _permissions = signal<PermissionMap>({});
  private readonly _systemRole = signal<SystemRole | null>(null);
  private readonly _loaded = signal(false);

  public readonly permissions = this._permissions.asReadonly();
  public readonly systemRole = this._systemRole.asReadonly();
  public readonly loaded = this._loaded.asReadonly();

  public readonly hasAdministrationAccess: Signal<boolean> = computed<boolean>(() => {
    return this.can(Permission.ACCESS_ADMINISTRATION);
  });
  public readonly canViewReminders = computed<boolean>(() => {
    return this.can(Permission.VIEW_REMINDER_LIST);
  });
  public readonly canCreateReminder = computed<boolean>(() => {
    return this.can(Permission.CREATE_REMINDER);
  });
  public readonly canEditReminder = computed<boolean>(() => {
    return this.can(Permission.EDIT_REMINDER);
  });
  public readonly canDeleteReminder = computed<boolean>(() => {
    return this.can(Permission.DELETE_REMINDER);
  });
  public readonly canActivateReminder = computed<boolean>(() => {
    return this.can(Permission.ACTIVATE_REMINDER);
  });
  public readonly canDeactivateReminder = computed<boolean>(() => {
    return this.can(Permission.DEACTIVATE_REMINDER);
  });

  /** @deprecated should be substituted with permissions and removed */
  public readonly isAdmin = computed<boolean>(() => {
    return this.can(Permission.VIEW_GROUP_LIST) || this._systemRole() === SystemRole.ADMIN;
  });
  public readonly canViewAdminGroups = computed<boolean>(() => {
    return this.can(Permission.VIEW_GROUP_LIST);
  });
  public readonly canCreateGroup = computed<boolean>(() => {
    return this.can(Permission.CREATE_GROUP);
  });
  public readonly canSearchGroups = computed<boolean>(() => {
    return this.can(Permission.SEARCH_GROUPS);
  });
  public readonly canViewArchivedGroup = computed<boolean>(() => {
    return this.can(Permission.VIEW_ARCHIVED_GROUPS);
  });
  public readonly canEditGroup = computed<boolean>(() => {
    return this.can(Permission.EDIT_GROUP);
  });
  public readonly canEditGroupLimited = computed<boolean>(() => {
    return this.can(Permission.EDIT_GROUP_LIMITED);
  });
  public readonly canArchiveGroup = computed<boolean>(() => {
    return this.can(Permission.ARCHIVE_GROUP);
  });
  public readonly canDeleteGroup = computed<boolean>(() => {
    return this.can(Permission.DELETE_GROUP);
  });
  public readonly canOpenGroupDetails = computed<boolean>(() => {
    return this.can(Permission.OPEN_GROUP_DETAILS);
  });
  public readonly canSendMessage = computed<boolean>(() => {
    return this.can(Permission.SEND_MESSAGE);
  });
  public readonly canAttachFiles = computed<boolean>(() => {
    return this.can(Permission.ATTACH_FILES);
  });
  // show group tags inside a group
  public readonly canViewGroupTags = computed<boolean>(() => {
    return this.can(Permission.VIEW_GROUP_TAGS);
  });
  public readonly canViewTagsCatalog = computed<boolean>(() => {
    return this.can(Permission.VIEW_TAG_CATALOG);
  });
  public readonly canCreateTag = computed<boolean>(() => {
    return this.can(Permission.CREATE_TAG);
  });
  public readonly canEditTag = computed<boolean>(() => {
    return this.can(Permission.EDIT_TAG);
  });
  public readonly canDeleteTag = computed<boolean>(() => {
    return this.can(Permission.DELETE_TAG);
  });
  public readonly canAssignTags = computed<boolean>(() => {
    return this.can(Permission.ASSIGN_TAGS);
  });
  public readonly canConfigureGroupSettings = computed<boolean>(() => {
    return this.can(Permission.CONFIGURE_GROUP_SORTING);
  });
  public readonly canInitializeSystemTelegram = computed<boolean>(() => {
    return this.can(Permission.INITIALIZE_SYSTEM_TELEGRAM);
  });
  public readonly canChangeSystemTelegram = computed<boolean>(() => {
    return this.can(Permission.CHANGE_SYSTEM_TELEGRAM);
  });
  public readonly canReauthorizeSystemTelegram = computed<boolean>(() => {
    return this.can(Permission.REAUTHORIZE_SYSTEM_TELEGRAM);
  });
  public readonly canViewTelegramSettings = computed<boolean>(() => {
    return this.canInitializeSystemTelegram() || this.canChangeSystemTelegram() || this.canReauthorizeSystemTelegram();
  });

  public load(): Observable<CurrentUserPermissionsDto> {
    return this._userApiService.getCurrentUserPermissions().pipe(
      tap(({ permissions, systemRole }) => {
        this._permissions.set(permissions ?? {});
        this._systemRole.set(systemRole ?? null);
        this._loaded.set(true);
      })
    );
  }

  public can(permission: Permission): boolean {
    return this._permissions()[permission] === true;
  }

  public canAny(permissions: Permission[]): boolean {
    return permissions.some(permission => this.can(permission));
  }

  public canAll(permissions: Permission[]): boolean {
    return permissions.every(permission => this.can(permission));
  }

  public setPermissions(permissions: PermissionMap): void {
    this._permissions.set(permissions);
    this._loaded.set(true);
  }

  public clear(): void {
    this._permissions.set({});
    this._systemRole.set(null);
    this._loaded.set(false);
  }
}
